import { round2 } from './gst';

export const BASIC_INTELLIGENCE_PLANS = new Set(['basic', 'pro', 'scale']);

export function canUseBasicIntelligence(plan) {
  return BASIC_INTELLIGENCE_PLANS.has(plan);
}

export function invoiceAmount(invoice) {
  return Number(invoice?.taxSummary?.grandTotal || invoice?.grandTotal || 0);
}

export function invoiceTaxableAmount(invoice) {
  return Number(invoice?.taxSummary?.taxableAmount || 0);
}

function isReportableStatus(status) {
  return !['draft', 'cancelled'].includes(status);
}

export function customerName(invoice) {
  return invoice?.customer?.name || invoice?.customerName || 'Unknown customer';
}

export function effectiveStatus(invoice) {
  const status = invoice?.status || 'draft';
  if (status === 'sent' && invoice?.dueDate) {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const due = new Date(`${invoice.dueDate}T00:00:00`);
    if (!Number.isNaN(due.getTime()) && due < today) return 'overdue';
  }
  return status;
}

export function daysPastDue(invoice) {
  if (!invoice?.dueDate || effectiveStatus(invoice) !== 'overdue') return 0;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const due = new Date(`${invoice.dueDate}T00:00:00`);
  if (Number.isNaN(due.getTime())) return 0;
  return Math.max(0, Math.floor((today - due) / 86400000));
}

function monthKey(value) {
  if (!value) return '';
  return String(value).slice(0, 7);
}

function currentMonthKey() {
  return new Date().toISOString().slice(0, 7);
}

function parseNumber(value) {
  return Number(value || 0) || 0;
}

function invoiceCost(invoice) {
  return (invoice?.lineItems || []).reduce((sum, item) => {
    const qty = parseNumber(item.qty);
    const purchase = parseNumber(item.purchasePrice || item.costPrice || item.purchase || item.cost);
    return sum + qty * purchase;
  }, 0);
}

function lineTaxableAmount(item) {
  if (item.taxableAmount != null) return parseNumber(item.taxableAmount);
  const qty = parseNumber(item.qty);
  const rate = parseNumber(item.rate);
  const discount = parseNumber(item.discount);
  return qty * rate * (1 - discount / 100);
}

function bucketFor(invoice) {
  const status = effectiveStatus(invoice);
  if (status !== 'sent' && status !== 'overdue') return null;
  const days = daysPastDue(invoice);
  if (status === 'sent' || days <= 0) return 'current';
  if (days <= 30) return '0_30';
  if (days <= 60) return '31_60';
  if (days <= 90) return '61_90';
  return '90_plus';
}

export function buildInsights(invoices = [], customers = [], items = []) {
  const month = currentMonthKey();
  const initialBuckets = {
    current: { label: 'Current', amount: 0, count: 0 },
    '0_30': { label: '0-30', amount: 0, count: 0 },
    '31_60': { label: '31-60', amount: 0, count: 0 },
    '61_90': { label: '61-90', amount: 0, count: 0 },
    '90_plus': { label: '90+', amount: 0, count: 0 },
  };
  const customerMap = new Map();
  const itemMap = new Map();
  const monthMap = new Map();
  const gst = { taxable: 0, cgst: 0, sgst: 0, igst: 0, total: 0 };

  const totals = invoices.reduce((acc, invoice) => {
    const status = effectiveStatus(invoice);
    const amount = invoiceAmount(invoice);
    const taxable = invoiceTaxableAmount(invoice);
    const cost = invoiceCost(invoice);
    const reportable = isReportableStatus(status);
    const profit = taxable - cost;
    const invMonth = monthKey(invoice.date || invoice.created_at);
    const isThisMonth = invMonth === month;

    if (reportable) {
      acc.invoiceValue += amount;
      acc.taxableRevenue += taxable;
      acc.grossProfit += profit;
      acc.totalCost += cost;
      acc.reportableInvoices += 1;
    }
    acc.statusCounts[status] = (acc.statusCounts[status] || 0) + 1;
    if (status === 'sent' || status === 'overdue') acc.receivable += amount;
    if (status === 'sent') acc.pending += amount;
    if (status === 'overdue') acc.overdue += amount;
    if (status === 'paid') acc.received += amount;
    if (isThisMonth && reportable) {
      acc.billedThisMonth += amount;
      acc.gstThisMonth += parseNumber(invoice?.taxSummary?.cgst) + parseNumber(invoice?.taxSummary?.sgst) + parseNumber(invoice?.taxSummary?.igst);
      acc.taxableThisMonth += parseNumber(invoice?.taxSummary?.taxableAmount);
      if (status === 'paid') acc.receivedThisMonth += amount;
    }

    const bucket = bucketFor(invoice);
    if (bucket) {
      acc.aging[bucket].amount += amount;
      acc.aging[bucket].count += 1;
    }

    const name = customerName(invoice);
    const customer = customerMap.get(name) || {
      name,
      billed: 0,
      paid: 0,
      outstanding: 0,
      overdue: 0,
      invoices: 0,
      lastInvoiceDate: '',
      taxableRevenue: 0,
      profit: 0,
    };
    customer.invoices += 1;
    if (reportable) {
      customer.billed += amount;
      customer.taxableRevenue += taxable;
      customer.profit += profit;
    }
    if (status === 'paid') customer.paid += amount;
    if (status === 'sent' || status === 'overdue') customer.outstanding += amount;
    if (status === 'overdue') customer.overdue += amount;
    if ((invoice.date || '') > customer.lastInvoiceDate) customer.lastInvoiceDate = invoice.date || '';
    customerMap.set(name, customer);

    if (reportable) (invoice.lineItems || []).forEach((item) => {
      const description = item.description || 'Untitled item';
      const qty = parseNumber(item.qty);
      const sale = lineTaxableAmount(item);
      const itemCost = parseNumber(item.purchasePrice || item.costPrice || item.purchase || item.cost) * qty;
      const row = itemMap.get(description) || { description, revenue: 0, cost: 0, profit: 0, qty: 0 };
      row.revenue += sale;
      row.cost += itemCost;
      row.profit += sale - itemCost;
      row.qty += qty;
      itemMap.set(description, row);
    });

    if (invMonth && reportable) {
      const monthly = monthMap.get(invMonth) || { month: invMonth, revenue: 0, invoices: 0 };
      monthly.revenue += taxable;
      monthly.invoices += 1;
      monthMap.set(invMonth, monthly);
    }

    if (reportable) {
      gst.taxable += parseNumber(invoice?.taxSummary?.taxableAmount);
      gst.cgst += parseNumber(invoice?.taxSummary?.cgst);
      gst.sgst += parseNumber(invoice?.taxSummary?.sgst);
      gst.igst += parseNumber(invoice?.taxSummary?.igst);
      gst.total += parseNumber(invoice?.taxSummary?.cgst) + parseNumber(invoice?.taxSummary?.sgst) + parseNumber(invoice?.taxSummary?.igst);
    }
    return acc;
  }, {
    invoices: invoices.length,
    customers: customers.length,
    items: items.length,
    invoiceValue: 0,
    taxableRevenue: 0,
    reportableInvoices: 0,
    billedThisMonth: 0,
    taxableThisMonth: 0,
    gstThisMonth: 0,
    receivable: 0,
    received: 0,
    receivedThisMonth: 0,
    pending: 0,
    overdue: 0,
    grossProfit: 0,
    totalCost: 0,
    statusCounts: {},
    aging: initialBuckets,
  });

  const customerLedger = [...customerMap.values()]
    .map((row) => ({
      ...row,
      billed: round2(row.billed),
      taxableRevenue: round2(row.taxableRevenue),
      paid: round2(row.paid),
      outstanding: round2(row.outstanding),
      overdue: round2(row.overdue),
      profit: round2(row.profit),
    }))
    .sort((a, b) => b.outstanding - a.outstanding || b.billed - a.billed);

  const itemPerformance = [...itemMap.values()]
    .map((row) => ({
      ...row,
      revenue: round2(row.revenue),
      cost: round2(row.cost),
      profit: round2(row.profit),
      margin: row.revenue ? Math.round((row.profit / row.revenue) * 100) : 0,
    }))
    .sort((a, b) => b.revenue - a.revenue);

  const monthlyTrend = [...monthMap.values()].sort((a, b) => a.month.localeCompare(b.month));
  const paidInvoices = invoices.filter((invoice) => effectiveStatus(invoice) === 'paid');
  const unpaidInvoices = invoices.filter((invoice) => ['sent', 'overdue'].includes(effectiveStatus(invoice)));

  return {
    ...totals,
    invoiceValue: round2(totals.invoiceValue),
    taxableRevenue: round2(totals.taxableRevenue),
    billedThisMonth: round2(totals.billedThisMonth),
    taxableThisMonth: round2(totals.taxableThisMonth),
    gstThisMonth: round2(totals.gstThisMonth),
    receivable: round2(totals.receivable),
    received: round2(totals.received),
    receivedThisMonth: round2(totals.receivedThisMonth),
    pending: round2(totals.pending),
    overdue: round2(totals.overdue),
    grossProfit: round2(totals.grossProfit),
    grossMargin: totals.taxableRevenue ? Math.round((totals.grossProfit / totals.taxableRevenue) * 100) : 0,
    collectionRate: (totals.received + totals.receivable) ? Math.round((totals.received / (totals.received + totals.receivable)) * 100) : 0,
    averageInvoiceValue: totals.reportableInvoices ? round2(totals.invoiceValue / totals.reportableInvoices) : 0,
    gst: {
      taxable: round2(gst.taxable),
      cgst: round2(gst.cgst),
      sgst: round2(gst.sgst),
      igst: round2(gst.igst),
      total: round2(gst.total),
    },
    customerLedger,
    topCustomers: customerLedger.slice(0, 5),
    itemPerformance,
    topItems: itemPerformance.slice(0, 5),
    monthlyTrend,
    paidInvoices,
    unpaidInvoices,
    needsFollowup: unpaidInvoices
      .filter((invoice) => effectiveStatus(invoice) === 'overdue')
      .sort((a, b) => daysPastDue(b) - daysPastDue(a))
      .slice(0, 8),
  };
}

export function rowsToCsv(rows) {
  return rows.map((row) => row.map((cell) => `"${String(cell ?? '').replace(/"/g, '""')}"`).join(',')).join('\n');
}

export function downloadCsv(filename, rows) {
  const url = URL.createObjectURL(new Blob([rowsToCsv(rows)], { type: 'text/csv;charset=utf-8' }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
